import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyPermissions } from '../hooks/useMyPermissions';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import { DataTable, useDataTable, type DataTableFilter, type DataTableRefs } from '@odo/shared-ui';
import { usePageHeader } from '../providers/PageHeaderProvider';
import { usePageTitle } from '../hooks/usePageTitle';
import { useViewportPageSize } from '../hooks/useViewportPageSize';
import { useToast } from '../providers/ToastProvider';
import { apiErrorMessage } from '../utils/apiError';
import { useEvents, useDeleteEvent, useUpdateEvent } from '../hooks/useEvents';
import { DeleteEventModal } from '../components/events/DeleteEventModal';
import { buildEventColumns, type EventsSortBy } from '../components/events/eventColumns';
import {
    EVENT_STATUS_LABEL,
    eventStatus,
    type EventStatus
} from '../components/events/eventStatus';
import { EventStatCards, type EventQuickStat } from '../components/events/EventStatCards';
import type { EventListRow } from '../services/events';

// The small admin catalog is fetched whole and searched/sorted/paginated
// client-side. 100 is the shared v2 `paginationSchema`'s hard `max`.
const FULL_CATALOG_LIMIT = 100;
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

type EventFilters = { status: string[] };

const SKELETON_ROW_COUNT = 6;

function SkeletonRows() {
    // Widths track the five real columns, so the skeleton reads as this table
    // rather than a generic bar chart - same approach as PracticesTable.
    const widths = ['w-52', 'w-16', 'w-28', 'w-40', 'w-8'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-14">
                    {widths.map((width, j) => (
                        <td key={j} className="px-3 first:pl-5 align-middle">
                            <div className={`h-3.5 rounded bg-gray-200 animate-pulse ${width}`} />
                        </td>
                    ))}
                    <td className="px-3 last:pr-4 align-middle" />
                </tr>
            ))}
        </>
    );
}

interface EmptyStateProps {
    isError: boolean;
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
    /** Null without `events.update` - the empty state then offers no way in. */
    onNewEvent: (() => void) | null;
}

// Mirrors PracticesTable's EmptyState, plus an error branch - "No events"
// would blame the data for what's actually a failed request.
function EmptyState({ isError, search, hasFilters, onClearFilters, onNewEvent }: EmptyStateProps) {
    if (isError) {
        return (
            <div className="flex flex-col items-center gap-3 text-center">
                <MegaphoneIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
                <p className="text-sm text-gray-500">
                    Couldn't load events. The table is empty because the request failed, not because
                    there are none.
                </p>
            </div>
        );
    }

    const hasSearch = search.length > 0;
    const isFiltered = hasSearch || hasFilters;
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <MegaphoneIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">
                {hasSearch ? (
                    <>No events match &ldquo;{search}&rdquo;.</>
                ) : hasFilters ? (
                    'No events match your filters.'
                ) : onNewEvent ? (
                    'No events yet. Create one and it goes live on the dashboard banners without a deploy.'
                ) : (
                    'No events yet.'
                )}
            </p>
            <div className="flex gap-2.5 mt-1">
                {isFiltered && (
                    <button
                        type="button"
                        onClick={onClearFilters}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        {hasFilters ? 'Clear filters' : 'Clear search'}
                    </button>
                )}
                {onNewEvent && (
                    <button
                        type="button"
                        onClick={onNewEvent}
                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors"
                    >
                        + New event
                    </button>
                )}
            </div>
        </div>
    );
}

function compareRows(a: EventListRow, b: EventListRow, sortBy: EventsSortBy): number {
    if (sortBy === 'title') return a.title.localeCompare(b.title);
    const key = sortBy;
    return new Date(a[key]).getTime() - new Date(b[key]).getTime();
}

export function EventsList() {
    usePageTitle('Events');
    const navigate = useNavigate();
    // Reading events is open to every files-viewer user; authoring is not. The
    // API refuses the write either way - this keeps the UI from offering it.
    const { hasPermission } = useMyPermissions();
    const canManage = hasPermission('events.update');
    // `events.delete` is its own IAM permission, separate from `events.update` -
    // an update-only admin must not see (and 403 on) "Delete event".
    const canDelete = hasPermission('events.delete');
    // PII (attendee email/name) - gated on `events.get`, separate from the
    // open-read list/detail pages and from `canManage`/`canDelete`.
    const canViewSignups = hasPermission('events.get');
    const { setTitle, setSubtitle } = usePageHeader();
    const { showToast } = useToast();

    const table = useDataTable<EventsSortBy, EventFilters>({
        defaultSort: { by: 'startsAt', order: 'desc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { status: [] },
        mode: 'paged'
    });
    const { status: statusFilter } = table.filters;

    const [search, setSearch] = useState('');
    const handleSearchChange = (value: string) => {
        setSearch(value);
        table.setPage(1);
    };

    const [deleteTarget, setDeleteTarget] = useState<EventListRow | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    // The stat cards are a one-at-a-time quick filter, on top of the multi-select
    // Status dropdown - same relationship as DiscountsPage's cards and filters.
    const [quickStat, setQuickStat] = useState<EventQuickStat | null>(null);

    const { events, isLoading, isError } = useEvents({ page: 1, limit: FULL_CATALOG_LIMIT });
    const { mutateAsync: updateEvent } = useUpdateEvent();
    const { mutateAsync: deleteEvent, isPending: isDeleting } = useDeleteEvent();

    useEffect(() => {
        setTitle('Events');
        setSubtitle(`${events.length} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [events.length, setTitle, setSubtitle]);

    const counts = useMemo(() => {
        const empty: Record<EventStatus, number> = {
            active: 0,
            upcoming: 0,
            past: 0,
            disabled: 0
        };
        for (const event of events) empty[eventStatus(event)] += 1;
        return empty;
    }, [events]);

    const totalSignups = useMemo(
        () => events.reduce((sum, event) => sum + event.signupCount, 0),
        [events]
    );

    const toggleQuickStat = useCallback((stat: EventQuickStat) => {
        setQuickStat((prev) => (prev === stat ? null : stat));
        table.setPage(1);
        // `table` is stable apart from its setters; depending on it whole would recreate this every page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        return events.filter((event) => {
            if (quickStat && eventStatus(event) !== quickStat) {
                return false;
            }
            if (
                statusFilter.length > 0 &&
                !statusFilter.includes(EVENT_STATUS_LABEL[eventStatus(event)])
            ) {
                return false;
            }
            if (!query) return true;
            return (
                event.title.toLowerCase().includes(query) ||
                event.id.toLowerCase().includes(query) ||
                event.topic.toLowerCase().includes(query)
            );
        });
    }, [events, search, statusFilter, quickStat]);

    const sorted = useMemo(() => {
        const direction = table.order === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => direction * compareRows(a, b, table.sort));
    }, [filtered, table.sort, table.order]);

    const { pageSize, tableCardRef, tableHeaderRef, tableBodyRef, paginationRef, remeasureOnce } =
        useViewportPageSize(VIEWPORT_DEFAULT_PAGE_SIZE);
    const { setLimit } = table;
    useEffect(() => {
        setLimit(pageSize);
    }, [pageSize, setLimit]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / table.limit));
    const currentPage = Math.min(table.page, totalPages);
    const paged = sorted.slice((currentPage - 1) * table.limit, currentPage * table.limit);

    useLayoutEffect(() => {
        if (paged.length > 0) remeasureOnce();
    }, [paged.length, remeasureOnce]);

    const handleToggleEnabled = useCallback(
        async (event: EventListRow) => {
            setTogglingId(event.id);
            try {
                await updateEvent({ id: event.id, patch: { enabled: !event.enabled } });
                showToast(event.enabled ? 'Event disabled' : 'Event enabled', 'success');
            } catch (error) {
                showToast(apiErrorMessage(error, 'Failed to update event'), 'error');
            } finally {
                setTogglingId(null);
            }
        },
        [updateEvent, showToast]
    );

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteEvent(deleteTarget.id);
            showToast('Event deleted', 'success');
            setDeleteTarget(null);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to delete event'), 'error');
        }
    };

    const columns = useMemo(
        () =>
            buildEventColumns({
                onEdit: (event) => navigate(`/accounts/events/${event.id}`),
                onDuplicate: (event) =>
                    navigate('/accounts/events/new', { state: { duplicateFrom: event } }),
                onToggleEnabled: (event) => {
                    void handleToggleEnabled(event);
                },
                onDelete: setDeleteTarget,
                onViewSignups: (event) => navigate(`/accounts/events/${event.id}?tab=signups`),
                togglingId,
                canManage,
                canDelete,
                canViewSignups
            }),
        [togglingId, handleToggleEnabled, navigate, canManage, canDelete, canViewSignups]
    );

    const filters: DataTableFilter[] = [
        {
            key: 'status',
            label: 'Status',
            multiple: true,
            options: Object.values(EVENT_STATUS_LABEL),
            value: statusFilter,
            onChange: (value) => table.setFilter('status', value),
            workQueueVariant: true,
            align: 'right',
            searchable: false
        }
    ];

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    const hasActiveFilters = statusFilter.length > 0 || quickStat !== null;

    const clearFilters = useCallback(() => {
        setSearch('');
        setQuickStat(null);
        table.setFilter('status', []);
        table.setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <EventStatCards
                counts={counts}
                totalSignups={totalSignups}
                active={quickStat}
                onToggle={toggleQuickStat}
            />

            <DataTable
                label="Events"
                columns={columns}
                rows={paged}
                getRowKey={(event) => event.id}
                // Opening the editor is the row's primary action, same as a
                // practice row opening the practice.
                rowAction={{
                    onActivate: (event) => navigate(`/accounts/events/${event.id}`),
                    mode: 'click'
                }}
                rowHeightClassName="h-14"
                tableLayout="auto"
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode="paged"
                pagination={{
                    page: currentPage,
                    totalPages,
                    total: sorted.length,
                    onPage: (p) => table.setPage(Math.min(Math.max(1, p), totalPages))
                }}
                isLoading={isLoading}
                loadingRows={<SkeletonRows />}
                emptyState={
                    <EmptyState
                        isError={isError}
                        search={search}
                        hasFilters={hasActiveFilters}
                        onClearFilters={clearFilters}
                        onNewEvent={canManage ? () => navigate('/accounts/events/new') : null}
                    />
                }
                emptyStateCellClassName="px-3 py-16"
                refs={refs}
                className="overflow-hidden px-8 pb-8"
                search={{
                    value: search,
                    onChange: handleSearchChange,
                    placeholder: 'Search title, slug, or topic…'
                }}
                filters={filters}
                actions={
                    canManage
                        ? [
                              {
                                  key: 'new-event',
                                  label: '+ New event',
                                  variant: 'primary' as const,
                                  onClick: () => navigate('/accounts/events/new')
                              }
                          ]
                        : []
                }
            />

            {deleteTarget && (
                <DeleteEventModal
                    event={deleteTarget}
                    onConfirm={() => {
                        void handleDelete();
                    }}
                    onClose={() => setDeleteTarget(null)}
                    isPending={isDeleting}
                />
            )}
        </div>
    );
}
